import React, { useState, useEffect } from "react";
import { useMyContext } from "../../../components/Provider/CommonProvider";
import { Card, Button, Table, Form, Dropdown } from "react-bootstrap";
import * as XLSX from "xlsx";
import "./Partner.css";
import { FaArrowUp, FaFileCsv, FaFileExcel, FaPaypal, FaStripeS, FaUniversity, FaEllipsisV, FaDownload } from "react-icons/fa";
import { Link } from "react-router-dom";
import { FaEllipsisVertical } from "react-icons/fa6";
import MessageBox from "../../../components/CommonComponents/MessageBox";


const PartnerPayout = ({ partnerId }) => {
    const { fetchRequest } = useMyContext();
    const BaseUrl = process.env.REACT_APP_Baseurl;
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedPartner, setSelectedPartner] = useState("All Partners");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState({ totalEarnings: 0, totalPaid: 0, pendingPayouts: 0 });
    const [loading, setLoading] = useState(false);

    const role = localStorage.getItem('role');
    const userId = localStorage.getItem('user_id');
    const effectivePartnerId = partnerId || (role === "4" ? userId : undefined);

    useEffect(() => {
        fetchPayoutData();
    }, [effectivePartnerId, selectedMonth, currentPage]);

    const fetchPayoutData = async () => {
        setLoading(true);
        try {
            const response = await fetchRequest({
                method: "GET",
                url: `${BaseUrl}/admin/partner/payouts`,
                params: {
                    partner_id: effectivePartnerId,
                    month: selectedMonth || undefined,
                    page: currentPage,
                    limit: itemsPerPage
                }
            });
            if (response?.status) {
                setTransactions(response.data || []);
                setStats(response.stats || { totalEarnings: 0, totalPaid: 0, pendingPayouts: 0 });
            }
        } catch (error) {
            console.error("Error fetching payouts:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSingleDelete = (id) => {
        console.log(`Deleting transaction with ID: ${id}`);
    };

    const filteredTransactions = transactions; // Filtering now handled by API params
    const totalPages = Math.ceil((transactions?.total || 0) / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleDownloadCSV = () => {
        if (!transactions.length) return;
        const headers = "Partner,Period,Amount,Status\n";
        const rows = transactions.map(transaction =>
            `${transaction.partner_name},${transaction.period},${transaction.amount},${transaction.status}`
        ).join("\n");

        const csvContent = "data:text/csv;charset=utf-8," + headers + rows;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "partner_payouts.csv");
        document.body.appendChild(link);
        link.click();
    };

    const handleDownloadExcel = () => {
        if (!transactions.length) return;
        const ws = XLSX.utils.json_to_sheet(transactions);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Partner Payouts");
        XLSX.writeFile(wb, "partner_payouts.xlsx");
    };

    return (
        <>
            <h4>Partner Payout Dashboard</h4>
            <div className="container mt-4">
                <div className="partner-dashboard d-flex justify-content-between mb-4 p-3">
                    <div className="d-flex gap-3">
                        <Form.Control 
                            type="month" 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(e.target.value)} 
                            className="w-auto"
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" onClick={handleDownloadCSV} disabled={!transactions.length}><FaFileCsv />CSV</Button>
                        <Button variant="outline-secondary" onClick={handleDownloadExcel} disabled={!transactions.length}><FaFileExcel />Excel</Button>
                    </div>
                </div>
                <div className="row text-center mb-4">
                    <div className="col-md-4">
                        <Card className="p-3 d-flex flex-column align-items-center shadow-sm border-0">
                            <span className="text-secondary small text-uppercase fw-bold">Total Earnings</span>
                            <span className="fw-bold text-dark fs-3">${stats.totalEarnings?.toLocaleString() || '0'}</span>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="p-3 d-flex flex-column align-items-center shadow-sm border-0">
                            <span className="text-secondary small text-uppercase fw-bold">Total Paid Out</span>
                            <span className="fw-bold text-dark fs-3">${stats.totalPaid?.toLocaleString() || '0'}</span>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="p-3 d-flex flex-column align-items-center shadow-sm border-0">
                            <span className="text-secondary small text-uppercase fw-bold">Pending Payouts</span>
                            <span className="fw-bold text-primary fs-3">${stats.pendingPayouts?.toLocaleString() || '0'}</span>
                        </Card>
                    </div>
                </div>

                <div className="transactions py-2">
                    <div className="d-flex justify-content-between align-items-center px-2">
                        <h5>Recent Transactions</h5>
                        <span className="text-primary">View All</span>
                    </div>
                    <div className="table-responsive">
                        <div className="table-wrapper">
                            <Table className="mt-3">
                                <thead className="table-light">
                                    <tr>
                                        <th>Partner</th>
                                        <th>Period</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTransactions.length > 0 ? (
                                        filteredTransactions.map((transaction) => (
                                            <tr key={transaction.id}>
                                                <td>{transaction.partner}</td>
                                                <td>{transaction.period}</td>
                                                <td>{transaction.amount}</td>
                                                <td>
                                                    <span className={`badge payout_status ${transaction.status === "Paid" ? "bg-success" : "bg-warning"}`}>
                                                        {transaction.status}
                                                    </span>
                                                </td>
                                                <td className="d-flex align-items-center gap-3">
                                                    <button className="p-0 border-0 bg-transparent d-flex align-items-center me-3">
                                                        <FaDownload size={18} className="text-dark" />
                                                    </button>

                                                    <div className="action-dropdown position-relative">
                                                        <span className="dropdown-button d-flex align-items-center">
                                                            <FaEllipsisVertical className="theme-text-color" />
                                                        </span>
                                                        <div className="action-menu theme-bg-color">
                                                            <Link to={`/monetization-detail/${transaction?.id}`} className="action-dropdown-item theme-text-color d-flex align-items-center">
                                                                <svg width="20" height="20" viewBox="0 0 24 24">
                                                                    <g>
                                                                        <path className="svgIcon" d="M10.3,18.5l-4.74.64A.49.49,0,0,1,5,18.59l.64-4.74a.42.42,0,0,1,.14-.28L17.31,2a.59.59,0,0,1,.85,0L22.11,6a.6.6,0,0,1,0,.84L10.58,18.36A.53.53,0,0,1,10.3,18.5Z" />
                                                                        <line className="svgIcon" x1="1.01" y1="21.58" x2="18.89" y2="21.58" />
                                                                    </g>
                                                                </svg>
                                                                <MessageBox text="View Detail" classname="ms-2 theme-text-color" />
                                                            </Link>

                                                            <div onClick={() => handleSingleDelete(transaction?.id)}
                                                                className="action-dropdown-item theme-text-color theme-bg-color">
                                                                <svg width="24" height="24" viewBox="0 0 24 24">
                                                                    <g>
                                                                        <rect className="svgIcon" x="4.38" y="5.54" width="14.49" height="17.71" />
                                                                        <line className="svgIcon" x1="1.16" y1="5.54" x2="22.09" y2="5.54" />
                                                                        <g>
                                                                            <line className="svgIcon" x1="9.21" y1="9.56" x2="9.21" y2="19.06" />
                                                                            <line className="svgIcon" x1="14.04" y1="9.56" x2="14.04" y2="19.06" />
                                                                        </g>
                                                                        <path className="svgIcon" d="M7.45,5.22a2.72,2.72,0,0,1,0-.28,4.19,4.19,0,1,1,8.37,0c0,.09,0,.19,0,.29" />
                                                                    </g>
                                                                </svg>
                                                                <MessageBox text="Delete" classname="ms-2 theme-text-color" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">No transactions found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>


                    <div className="pagination-container d-flex justify-content-between align-items-center p-3">
                        <span>
                            Showing {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} results
                        </span>
                        <div className="btn-group">
                            <Button variant="outline-secondary" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>&lt;</Button>
                            {[...Array(totalPages)].map((_, i) => (
                                <Button key={i} variant={currentPage === i + 1 ? "primary" : "outline-secondary"} onClick={() => handlePageChange(i + 1)}>
                                    {i + 1}
                                </Button>
                            ))}
                            <Button variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>&gt;</Button>
                        </div>
                    </div>
                </div>
                <div className="mt-4 p-3 Payment-method">
                    <h4>Payment Methods</h4>
                    <div className="row mt-2">
                        <div className="col-md-4">
                            <div className="card p-3">
                                <p><FaPaypal /> PayPal</p>
                                <span className="text-secondary">Connected</span>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card p-3">
                                <p><FaStripeS /> Stripe</p>
                                <span className="text-secondary">Connected</span>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card p-3">
                                <p><FaUniversity /> Bank Transfer</p>
                                <span className="text-secondary">Manual</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>

    );
};

export default PartnerPayout;
